// src/components/TakeAction/OpenDataActionModal.jsx
import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  TextField,
  Chip,
  Stack,
  Box,
} from "@mui/material"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import { IoLocationOutline } from "react-icons/io5"
import { MdOutlineEmail, MdOutlinePhoneInTalk } from "react-icons/md"
import { BsGlobe2 } from "react-icons/bs"
import { RxCross1 } from "react-icons/rx"
import { useUser } from "../../context/UserContext"
import { fetchRepresentativeInfo } from "../../utils/representativeUtils"
// import "../TakeActionComponent/TakeActionModal.scss"

const OpenDataActionModal = ({ open, onClose }) => {
  const {
    userRepsBioguideID,
    userFirstName,
    userLastName,
    userState,
    userDistrict,
  } = useUser()

  const [repInfo, setRepInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  const [copiedSubject, setCopiedSubject] = useState(false)
  const [copiedBody, setCopiedBody] = useState(false)

  // Basic user display info
  const displayName =
    [userFirstName, userLastName].filter(Boolean).join(" ") || "[Your Name]"

  const stateDistrict =
    [userState, userDistrict].filter(Boolean).join("-") || "[State-District]"

  // Subject + body templates – focused on open data / APIs
  const subjectTemplate = `${displayName} — Please support open, API-accessible state legislation data`

  const bodyTemplate = lastName =>
    [
      `Dear Representative ${lastName || "[Last Name]"},`,
      "",
      `I’m a constituent in ${stateDistrict}. I’m writing to ask that you support policies requiring state-level legislation to be published as open data with a public API.`,
      "",
      "When bills and statutes are open, machine-readable, and available through an API, it makes it easier for civic tools, journalists, researchers, and everyday residents to understand what is happening in our state government.",
      "",
      "Specifically, I’d like to see:",
      "• State legislation published in open, machine-readable formats (not just PDFs).",
      "• A public, well-documented API so developers can build tools that help people follow and understand the law.",
      "• Clear policies that keep this data free, timely, and accessible to everyone.",
      "",
      "Thank you for your service to our community and for considering this request.",
      "",
      "Sincerely,",
      displayName,
      stateDistrict,
    ].join("\n")

  const [subject, setSubject] = useState(subjectTemplate)
  const [body, setBody] = useState(bodyTemplate(null))

  // Load representative info (for name + contact links)
  useEffect(() => {
    if (!open || !userRepsBioguideID) return
    setLoading(true)
    setErr(null)

    fetchRepresentativeInfo(userRepsBioguideID)
      .then(data => {
        setRepInfo(data)
        setBody(bodyTemplate(data?.last_name))
      })
      .catch(e => {
        console.error(e)
        setErr("Failed to fetch representative data.")
      })
      .finally(() => setLoading(false))
  }, [open, userRepsBioguideID]) // eslint-disable-line react-hooks/exhaustive-deps

  // Copy helpers (no verification required)
  const handleCopySubject = async () => {
    try {
      await navigator.clipboard.writeText(subject || "")
      setCopiedSubject(true)
      setTimeout(() => setCopiedSubject(false), 2000)
    } catch {
      /* ignore */
    }
  }

  const handleCopyBody = async () => {
    try {
      await navigator.clipboard.writeText(body || "")
      setCopiedBody(true)
      setTimeout(() => setCopiedBody(false), 2000)
    } catch {
      /* ignore */
    }
  }

  if (!open) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(170, 168, 168, 0.45)" } },
        paper: {
          className: "open-data-action-modal",
          sx: {
            width: "min(1040px, 94vw)",
            maxWidth: "none",
            maxHeight: "92vh",
            boxShadow: "0px 4px 10px rgba(0,0,0,0.15)",
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogTitle
        className="take-action__title"
        sx={{ pb: 0.5, pt: 1.25, px: 2 }}
      >
        Ask for Open-Source, API-Accessible State Laws
      </DialogTitle>

      <DialogContent
        dividers
        className="take-action__content"
        sx={{ px: 2, py: 1.25 }}
      >
        {loading ? (
          <div className="take-action__loading" style={{ padding: 12 }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            {err && (
              <Typography color="error" sx={{ mb: 1 }}>
                {err}
              </Typography>
            )}

            {repInfo && (
              <>
                {/* NAME / PARTY */}
                <Typography
                  variant="h5"
                  className="take-action__rep-name"
                  sx={{ mb: 0.25 }}
                >
                  {repInfo.first_name} {repInfo.last_name}
                </Typography>
                <Typography
                  variant="subtitle2"
                  className="take-action__rep-details"
                  sx={{ mb: 1 }}
                >
                  {repInfo.party}
                </Typography>

                {/* CONTACT ROWS */}
                <Stack spacing={0.5} sx={{ mb: 1.25 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "nowrap",
                    }}
                  >
                    <IoLocationOutline size={18} />
                    <Typography variant="body2" sx={{ m: 0 }}>
                      Office: {repInfo.office || "Not available"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "nowrap",
                    }}
                  >
                    <MdOutlinePhoneInTalk size={18} />
                    {repInfo.phone ? (
                      <a
                        href={`tel:${repInfo.phone}`}
                        className="take-action__link"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {repInfo.phone}
                      </a>
                    ) : (
                      <Typography variant="body2" sx={{ m: 0 }}>
                        Not available
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "nowrap",
                    }}
                  >
                    <MdOutlineEmail size={18} />
                    {repInfo.contact_form ? (
                      <a
                        href={repInfo.contact_form}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="take-action__link"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {repInfo.contact_form}
                      </a>
                    ) : (
                      <Typography variant="body2" sx={{ m: 0 }}>
                        Not available
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "nowrap",
                    }}
                  >
                    <BsGlobe2 size={16} />
                    {repInfo.official_website_url ? (
                      <a
                        href={repInfo.official_website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="take-action__link"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {repInfo.official_website_url}
                      </a>
                    ) : (
                      <Typography variant="body2" sx={{ m: 0 }}>
                        Not available
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </>
            )}

            {/* GUIDANCE */}
            <Stack direction="row" spacing={0.75} sx={{ mb: 2, flexWrap: "wrap" }}>
              <Chip size="small" label="Keep it respectful" variant="outlined" />
              <Chip size="small" label="Avoid attachments" variant="outlined" />
            </Stack>

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                How to send your message
              </Typography>

              <Typography
                component="ul"
                variant="body2"
                sx={{
                  fontSize: "0.8rem",
                  color: "#000",
                  pl: 3,
                  mt: 0.5,
                  mb: 0,
                }}
              >
                <Typography
                  component="li"
                  variant="body2"
                  sx={{ fontSize: "0.8rem" }}
                >
                  Step 1: Edit the subject and message so they reflect your own
                  voice.
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{ fontSize: "0.8rem" }}
                >
                  Step 2: Copy them and paste into your representative&apos;s
                  official contact form, then submit.
                </Typography>
              </Typography>
            </Box>

            {/* SUBJECT + COPY */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <TextField
                label="Subject"
                fullWidth
                size="small"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                inputProps={{ maxLength: 160 }}
                helperText={`${subject.length}/160`}
              />
              <Button
                onClick={handleCopySubject}
                variant="contained"
                startIcon={<ContentCopyIcon />}
                sx={{
                  alignSelf: "flex-start",
                  mt: 0,
                  background: "linear-gradient(135deg, #047857 0%, #059669 100%)",
                  color: "#fff",
                  fontWeight: 800,
                  px: 2,
                  "&:hover": { opacity: 0.95 },
                  whiteSpace: "nowrap",
                }}
              >
                Copy
              </Button>
            </Box>
            {copiedSubject && (
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 1, color: "success.main" }}
              >
                Subject copied to clipboard.
              </Typography>
            )}

            {/* BODY + COPY */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <TextField
                label="Your Message"
                placeholder={bodyTemplate(repInfo?.last_name)}
                value={body}
                onChange={e => setBody(e.target.value)}
                fullWidth
                multiline
                minRows={8}
              />
              <Button
                onClick={handleCopyBody}
                variant="contained"
                startIcon={<ContentCopyIcon />}
                sx={{
                  alignSelf: "flex-start",
                  mt: 0,
                  background: "linear-gradient(135deg, #047857 0%, #059669 100%)",
                  color: "#fff",
                  fontWeight: 800,
                  px: 2,
                  "&:hover": { opacity: 0.95 },
                  whiteSpace: "nowrap",
                }}
              >
                Copy
              </Button>
            </Box>
            {copiedBody && (
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 1, color: "success.main" }}
              >
                Message copied to clipboard.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          startIcon={<RxCross1 />}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default OpenDataActionModal
